import { useCallback, useEffect } from 'react';
import { EElementCategory, IFieldElement, IFormState, IScheme, TElement } from '../types/calculator';
import { FieldErrors, FormState } from 'react-hook-form';
import { getEnrichedErrors, debouncedSubmitHandler, debouncedErrorHandler } from '../utils';

export interface IUseCalculatorCustomEventsDeps {
  formValues: IFormState;
  formState: FormState<IFormState>;
  eventTarget: HTMLFormElement | null;
  formScheme: TElement[];
}

const getFieldNamesWithValidation = (formScheme: IScheme[]) => {
  return formScheme[0].elements
    .filter(elem => elem.category === EElementCategory.Field && elem.validation)
    .map((elem: IFieldElement) => elem.name);
};

export const useCalculatorCustomEvents = ({
  formValues,
  eventTarget,
  formState,
  formScheme,
}: IUseCalculatorCustomEventsDeps) => {
  const { isValid, errors } = formState;

  const submitHandler = debouncedSubmitHandler(eventTarget);
  const errorHandler = debouncedErrorHandler(eventTarget);

  const handleError = useCallback(
    (currentErrors: FieldErrors<IFormState>) => {
      const enrichedErrors = getEnrichedErrors(currentErrors, formScheme as IScheme[]);

      const fieldOrder = getFieldNamesWithValidation(formScheme as IScheme[]);

      const sortedEnrichedErrorsArray = enrichedErrors.sort((a, b) => {
        return fieldOrder.indexOf(a.field) - fieldOrder.indexOf(b.field);
      });

      errorHandler(sortedEnrichedErrorsArray);
    },
    [errorHandler],
  );

  useEffect(() => {
    if (isValid) {
      submitHandler(formValues, errors);
    } else {
      handleError(errors);
    }
  }, [isValid, formValues, errors]);
};
